import { ButtonProps } from '@/components/button';
import { SubscriptionPlan, UserEntitlementInterface, UserSubscriptionInterface } from '@/types';
import { checkIsFreePlan, getSubscribeButtonText } from '@/utils/subscriptions';

import { PlanItemParams } from '../item';

export const getPlanParams = (
  plan: SubscriptionPlan,
  hasSession: boolean,
  subscription: UserSubscriptionInterface | undefined,
  selectedPlanId: string | undefined,
): PlanItemParams => {
  const isFreePlan = checkIsFreePlan(plan);
  const planIsSelected = selectedPlanId === plan.id;
  const buttonText = getSubscribeButtonText(hasSession, subscription, planIsSelected, isFreePlan);
  const buttonVariant = (isFreePlan ? 'primaryLightOutline' : 'primaryLight') as ButtonProps['variant'];
  const promoText = isFreePlan ? 'No credit card required' : 'No long term commitment, cancel anytime';

  return {
    plan,
    isSelected: planIsSelected,
    buttonText,
    buttonVariant,
    promoText,
  };
};

// Configuration for feature display formatting
const featureDisplayConfig: {
  [key: string]: {
    displayName: string;
    formatValue?: (value: any) => string;
    priority: number;
    hidden?: boolean; // Indicates that this feature should not be shown
    upgradedBy?: string; // Indicates that this feature is superseded by another
  };
} = {
  memberLimit: {
    displayName: '',
    formatValue: (value) => `${value} ${Number(value) === 1 ? 'user' : 'users'}`,
    priority: 100,
  },
  routeSearches: {
    displayName: 'Route search',
    formatValue: (value) => `${value} / month`,
    priority: 90,
  },
  scheduleSearches: {
    displayName: 'Schedule search',
    formatValue: (value) => `${value} / month`,
    priority: 85,
  },
  emissions: {
    displayName: 'Emissions estimates',
    priority: 75,
    upgradedBy: 'emissionsCalculator',
  },
  emissionsCalculator: {
    displayName: 'Certified emissions reports',
    priority: 70,
  },
  tracking: {
    displayName: 'Shipment tracking',
    priority: 66,
    upgradedBy: 'freeShipmentsPerMonth',
  },
  freeShipmentsPerMonth: {
    displayName: 'Shipment tracking',
    formatValue: (value) => `${value} free / month`,
    priority: 65,
  },
  rates: {
    displayName: 'Market index rates (routing only)',
    priority: 64,
    upgradedBy: 'rateManagement',
  },
  rateManagement: {
    displayName: 'Market index rates + dashboard',
    priority: 62,
  },
  whitelabel: {
    displayName: 'Custom branding & multi-tenant',
    priority: 61,
  },
  advancedTracking: {
    displayName: 'Delay, ETA and incident alerts',
    priority: 60,
    upgradedBy: 'whitelabel',
  },
  api: {
    displayName: 'API access',
    priority: 58,
  },
  insights: {
    displayName: 'Insights',
    priority: 50,
  },
  advancedSearch: {
    displayName: 'Advanced route search',
    priority: 35,
  },
  vesselFinder: {
    displayName: 'Vessel finder',
    priority: 25,
  },
  prioritySupport: {
    displayName: 'Priority support',
    priority: 10,
  },
};

const allPossibleFeatures = Object.keys(featureDisplayConfig);

interface DisplayFeature {
  key: string;
  display: string;
  limitText?: string;
  excluded: boolean;
  priority: number;
}

export const transformEntitlementsToFeatures = (
  entitlements: UserEntitlementInterface,
  maxFeaturesToShow: number = 0,
  showExcluded: boolean = true,
): DisplayFeature[] => {
  const result: DisplayFeature[] = [];
  const hiddenDueToUpgrades = new Set<string>();

  // Helper function to process a single feature
  const processFeature = (key: string, value: any, excluded: boolean = false): DisplayFeature | null => {
    const config = featureDisplayConfig[key];
    if (!config) return null;

    const { displayName, formatValue, priority } = config;

    let displayText = config.displayName;
    let limitText = '';

    // Format the value if applicable
    if (formatValue) {
      if (!displayName) {
        displayText = formatValue(value);
      } else if (value) {
        limitText = formatValue(value);
      }
    } else if (value === true) {
      displayText = `${displayText}`;
    } else if (typeof value === 'number' || typeof value === 'string') {
      displayText = `${displayText} ${value}`;
    }

    return {
      key,
      display: displayText,
      limitText,
      excluded,
      priority,
    };
  };

  // Identify features hidden due to upgrades
  Object.keys(entitlements).forEach((key) => {
    const config = featureDisplayConfig[key];
    if (!config) return;

    Object.entries(featureDisplayConfig).forEach(([featureKey, featureConfig]) => {
      if (featureConfig.upgradedBy === key && entitlements[featureKey] !== undefined) {
        hiddenDueToUpgrades.add(featureKey);
      }
    });
  });

  // Process included features
  Object.keys(entitlements).forEach((key) => {
    if (hiddenDueToUpgrades.has(key)) return;

    const feature = processFeature(key, entitlements[key]);
    if (feature) result.push(feature);
  });

  // Add excluded features if `showExcluded` is true
  if (showExcluded) {
    const includedKeys = new Set(Object.keys(entitlements));

    allPossibleFeatures.forEach((key) => {
      const config = featureDisplayConfig[key];
      if (!config || includedKeys.has(key) || config.hidden) return;

      // Skip basic features if their upgraded version is excluded too
      if (config.upgradedBy && !includedKeys.has(config.upgradedBy)) return;

      const excludedFeature = processFeature(key, null, true);
      if (excludedFeature) result.push(excludedFeature);
    });
  }

  // Sort features by inclusion status and priority
  result.sort((a, b) => {
    if (a.excluded !== b.excluded) {
      return a.excluded ? 1 : -1;
    }
    return b.priority - a.priority;
  });

  return maxFeaturesToShow > 0 ? result.slice(0, maxFeaturesToShow) : result;
};
